import React from 'react';
import { Plus, X, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { LearningOutcome } from '@/services/openaiService';

const BLOOM_LEVELS = [
  { value: 'Remember', description: 'Recall facts and basic concepts' },
  { value: 'Understand', description: 'Explain ideas or concepts' },
  { value: 'Apply', description: 'Use information in new situations' },
  { value: 'Analyze', description: 'Draw connections among ideas' },
  { value: 'Evaluate', description: 'Justify a stand or decision' },
  { value: 'Create', description: 'Produce new or original work' },
];

interface OutcomesInputProps {
  outcomes: LearningOutcome[];
  onChange: (outcomes: LearningOutcome[]) => void;
}

export const OutcomesInput: React.FC<OutcomesInputProps> = ({
  outcomes,
  onChange,
}) => {
  const [mode, setMode] = React.useState<'simple' | 'structured'>('simple');
  const [simpleText, setSimpleText] = React.useState('');

  // Convert simple text to outcomes array
  const parseSimpleText = (text: string): LearningOutcome[] => {
    if (!text.trim()) return [];
    
    // Split by newlines and filter empty lines
    const lines = text.split('\n').filter(line => line.trim());
    
    return lines.map((line, index) => {
      // Try to detect Bloom's level from keywords in the text
      const lowerLine = line.toLowerCase();
      let detectedBloom = 'Understand'; // default
      
      if (/\b(remember|recall|list|name|define|identify)\b/.test(lowerLine)) {
        detectedBloom = 'Remember';
      } else if (/\b(understand|explain|describe|summarize|interpret)\b/.test(lowerLine)) {
        detectedBloom = 'Understand';
      } else if (/\b(apply|use|implement|demonstrate|solve)\b/.test(lowerLine)) {
        detectedBloom = 'Apply';
      } else if (/\b(analyze|compare|contrast|examine|differentiate)\b/.test(lowerLine)) {
        detectedBloom = 'Analyze';
      } else if (/\b(evaluate|judge|assess|critique|justify)\b/.test(lowerLine)) {
        detectedBloom = 'Evaluate';
      } else if (/\b(create|design|develop|construct|produce)\b/.test(lowerLine)) {
        detectedBloom = 'Create';
      }

      return {
        title: `Outcome ${index + 1}`,
        bloom: detectedBloom,
        description: line.trim(),
      };
    });
  };

  // Handle simple text change
  const handleSimpleTextChange = (text: string) => {
    setSimpleText(text);
    const parsedOutcomes = parseSimpleText(text);
    onChange(parsedOutcomes);
  };

  // Handle mode switch
  const handleModeSwitch = () => {
    if (mode === 'simple') {
      // Switch to structured - outcomes already parsed
      setMode('structured');
    } else {
      // Switch to simple - convert outcomes back to text
      const text = outcomes.map(o => o.description).join('\n');
      setSimpleText(text);
      setMode('simple');
    }
  };

  // Add new structured outcome
  const addOutcome = () => {
    onChange([
      ...outcomes,
      { title: `Outcome ${outcomes.length + 1}`, bloom: 'Understand', description: '' },
    ]);
  };

  // Update a structured outcome
  const updateOutcome = (index: number, field: keyof LearningOutcome, value: string) => {
    const updated = [...outcomes];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  // Remove a structured outcome
  const removeOutcome = (index: number) => {
    onChange(outcomes.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold">
          Learning Outcomes <span className="text-destructive">*</span>
        </Label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleModeSwitch}
                className="gap-2"
              >
                {mode === 'simple' ? (
                  <>
                    <ToggleLeft className="h-4 w-4" />
                    Simple Mode
                  </>
                ) : (
                  <>
                    <ToggleRight className="h-4 w-4" />
                    Structured Mode
                  </>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {mode === 'simple'
                  ? 'Switch to structured mode for detailed outcome editing'
                  : 'Switch to simple mode for quick text entry'}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {mode === 'simple' ? (
        <div className="space-y-2">
          <Textarea
            placeholder="Enter your learning outcomes (one per line):

Example:
Students will be able to analyze complex datasets using statistical methods
Students will demonstrate critical thinking through written argumentation
Students will apply programming concepts to solve real-world problems"
            value={simpleText}
            onChange={(e) => handleSimpleTextChange(e.target.value)}
            rows={6}
            className="resize-y"
          />
          <p className="text-xs text-muted-foreground">
            Enter one outcome per line. Bloom's taxonomy levels will be auto-detected from keywords.
          </p>
          {outcomes.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {outcomes.map((outcome, index) => (
                <Badge key={index} variant="outline" className="gap-1">
                  {outcome.bloom}
                </Badge>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {outcomes.map((outcome, index) => (
            <div
              key={index}
              className="border rounded-lg p-4 space-y-3 bg-muted/30 animate-fade-in"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">
                  Outcome {index + 1}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => removeOutcome(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor={`title-${index}`}>Title</Label>
                  <Input
                    id={`title-${index}`}
                    value={outcome.title}
                    onChange={(e) => updateOutcome(index, 'title', e.target.value)}
                    placeholder="e.g., Critical Analysis"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor={`bloom-${index}`}>Bloom's Level</Label>
                  <Select
                    value={outcome.bloom}
                    onValueChange={(value) => updateOutcome(index, 'bloom', value)}
                  >
                    <SelectTrigger id={`bloom-${index}`}>
                      <SelectValue placeholder="Select level..." />
                    </SelectTrigger>
                    <SelectContent>
                      {BLOOM_LEVELS.map((level) => (
                        <SelectItem key={level.value} value={level.value}>
                          <div className="flex flex-col">
                            <span>{level.value}</span>
                            <span className="text-xs text-muted-foreground">
                              {level.description}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor={`desc-${index}`}>Description</Label>
                <Textarea
                  id={`desc-${index}`}
                  value={outcome.description}
                  onChange={(e) => updateOutcome(index, 'description', e.target.value)}
                  placeholder="Describe what students will be able to do..."
                  rows={2}
                />
              </div>
            </div>
          ))}
          
          <Button
            type="button"
            variant="outline"
            onClick={addOutcome}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Learning Outcome
          </Button>
        </div>
      )}

      {outcomes.length > 0 && (
        <p className="text-sm text-muted-foreground">
          ✓ {outcomes.length} outcome{outcomes.length !== 1 ? 's' : ''} configured
        </p>
      )}
    </div>
  );
};

export default OutcomesInput;

